import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  CaretLeft, TrashSimple, Plus, Star, StarFour, Camera, PencilSimple,
  User as UserIcon, ForkKnife, Ruler, Image as ImageIcon, BellRinging, LockKey,
  Ticket, X, Warning, ClockCounterClockwise, Clock,
} from "@phosphor-icons/react";
import MeasurementForm from "@/components/MeasurementForm";
import MeasurementsChart from "@/components/MeasurementsChart";
import MeasurementsCompare from "@/components/MeasurementsCompare";
import BmiTdeeCard from "@/components/BmiTdeeCard";
import PhotoUploader from "@/components/PhotoUploader";
import PhotoGallery from "@/components/PhotoGallery";
import SmartDateInput from "@/components/SmartDateInput";
import { formatItalianDate } from "@/lib/dateFormat";
import { ACTIVITY_OPTIONS } from "@/lib/bmiTdee";
import { useConfirmDialog } from "@/components/ConfirmDialog";

const SECTIONS = [
  { key: "profile", label: "Profilo", icon: UserIcon, hint: "Dati anagrafici" },
  { key: "plans", label: "Piano nutrizionale", icon: ForkKnife, hint: "Pasti, orari, porzioni" },
  { key: "days", label: "Giornate cliente", icon: ClockCounterClockwise, hint: "Salvataggi porzionario" },
  { key: "measurements", label: "Rilievi corporei", icon: Ruler, hint: "Peso, BF, circonferenze" },
  { key: "photos", label: "Foto rilievi", icon: ImageIcon, hint: "Galleria cloud" },
];

export default function AdminClientDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [plans, setPlans] = useState([]);
  const [compositions, setCompositions] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openCompositionId, setOpenCompositionId] = useState(null);

  // Sort measurements descending by date once per data change — used both by
  // BmiTdeeCard (latest = index 0) and the measurement list rendering.
  const measurementsDesc = useMemo(
    () => [...measurements].sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [measurements]
  );
  const [editingMeasurement, setEditingMeasurement] = useState(null);
  const [addingMeasurement, setAddingMeasurement] = useState(false);
  const measurementFormOpen = !!editingMeasurement || addingMeasurement;
  const section = searchParams.get("sec") || "profile";
  const setSection = (k) => setSearchParams(k === "profile" ? {} : { sec: k }, { replace: true });

  const { confirm, dialog } = useConfirmDialog();

  const loadAll = async () => {
    const [{ data: c }, { data: p }, { data: m }, { data: ph }, { data: cx }] = await Promise.all([
      api.get(`/clients/${id}`),
      api.get(`/clients/${id}/plans`),
      api.get(`/clients/${id}/measurements`),
      api.get(`/clients/${id}/photos`),
      api.get(`/clients/${id}/compositions`).catch(() => ({ data: [] })),
    ]);
    setClient(c); setPlans(p); setMeasurements(m); setPhotos(ph); setCompositions(cx || []);
  };
  useEffect(() => { loadAll().catch(() => toast.error("Errore caricamento")); }, [id]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/clients/${id}`, {
        name: client.name,
        email: client.email,
        gender: client.gender,
        date_of_birth: client.date_of_birth,
        height_cm: client.height_cm ? Number(client.height_cm) : null,
        activity_level: client.activity_level || null,
        email_notifications: client.email_notifications !== false,
        password: client.password || null,
        notes: client.notes,
        sessions_total: Number.isFinite(Number(client.sessions_total)) ? Math.max(0, Math.floor(Number(client.sessions_total))) : 0,
      });
      setClient({ ...data, password: "" });
      toast.success("Profilo aggiornato");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Errore aggiornamento");
    } finally { setSaving(false); }
  };

  // Sessions pack — computed on demand from the client doc.
  const attendance = client?.attendance || [];
  const sessionsPaid = attendance.filter((a) => a.paid).length;
  const sessionsTotal = Number(client?.sessions_total) || 0;
  const sessionsRemaining = sessionsTotal - sessionsPaid;
  const attendanceDesc = useMemo(
    () => [...(client?.attendance || [])].sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [client]
  );

  const toggleAttendancePaid = async (att) => {
    try {
      const { data } = await api.patch(`/clients/${id}/attendance/${att.id}`, { paid: !att.paid });
      setClient({ ...client, attendance: data.attendance });
    } catch (e) { toast.error("Errore aggiornamento seduta"); }
  };

  const deleteAttendance = async (att) => {
    const ok = await confirm({
      title: "Annullare la presenza?",
      description: `La presenza del ${att.date} sarà rimossa dallo storico. Il rilievo collegato (se presente) non verrà toccato.`,
      confirmLabel: "Annulla presenza",
    });
    if (!ok) return;
    try {
      const { data } = await api.delete(`/clients/${id}/attendance/${att.id}`);
      setClient({ ...client, attendance: data.attendance });
      toast.success("Presenza annullata");
    } catch (e) { toast.error("Errore annullamento"); }
  };

  const createPlan = () => {
    // Do NOT hit the API here — open the editor with an in-memory draft plan.
    // The plan is created in DB only when the admin clicks "Salva" inside the editor.
    navigate(`/admin/plans/new?client=${id}`);
  };

  const activatePlan = async (planId) => {
    try {
      await api.patch(`/plans/${planId}`, { active: true });
      toast.success("Piano attivato");
      loadAll();
    } catch (e) { toast.error("Errore attivazione"); }
  };

  const deactivatePlan = async (planId) => {
    try {
      await api.patch(`/plans/${planId}`, { active: false });
      toast.success("Piano disattivato");
      loadAll();
    } catch (e) { toast.error("Errore disattivazione"); }
  };

  const deletePlan = async (planId, planName) => {
    const ok = await confirm({
      title: "Eliminare il piano?",
      description: `«${planName || "Piano"}» verrà eliminato definitivamente. Questa azione non può essere annullata.`,
      confirmLabel: "Elimina piano",
    });
    if (!ok) return;
    try {
      await api.delete(`/plans/${planId}`);
      toast.success("Piano eliminato");
      loadAll();
    } catch (e) {
      toast.error("Errore eliminazione piano: " + (e?.response?.data?.detail || e.message || ""));
    }
  };

  const deleteComposition = async (comp) => {
    const dateStr = (() => {
      const [y, mo, dd] = (comp.date || "").split("-");
      return y ? `${dd}/${mo}/${y}` : comp.date || "";
    })();
    const ok = await confirm({
      title: "Eliminare il salvataggio?",
      description: `La giornata «${comp.plan_name || comp.label || dateStr}» del ${dateStr} sarà rimossa definitivamente.`,
      confirmLabel: "Elimina salvataggio",
    });
    if (!ok) return;
    try {
      await api.delete(`/compositions/${comp.composition_id}`);
      setCompositions((prev) => prev.filter((c) => c.composition_id !== comp.composition_id));
      if (openCompositionId === comp.composition_id) setOpenCompositionId(null);
      toast.success("Salvataggio eliminato");
    } catch (e) {
      toast.error("Errore eliminazione: " + (e?.response?.data?.detail || e.message || ""));
    }
  };

  const deleteMeasurement = async (mid, mDate) => {
    const ok = await confirm({
      title: "Eliminare il rilievo?",
      description: `Il rilievo del ${mDate} sarà rimosso definitivamente.`,
      confirmLabel: "Elimina rilievo",
    });
    if (!ok) return;
    try {
      await api.delete(`/measurements/${mid}`);
      toast.success("Rilievo eliminato");
      if (editingMeasurement?.measurement_id === mid) setEditingMeasurement(null);
      loadAll();
    } catch (e) {
      toast.error("Errore eliminazione: " + (e?.response?.data?.detail || e.message || ""));
    }
  };

  const deletePhoto = async (pid) => {
    const ok = await confirm({
      title: "Eliminare la foto?",
      description: "La foto verrà rimossa anche da Cloudinary.",
      confirmLabel: "Elimina foto",
    });
    if (!ok) return;
    try {
      await api.delete(`/photos/${pid}`);
      toast.success("Foto eliminata");
      loadAll();
    } catch (e) { toast.error("Errore eliminazione foto"); }
  };

  const saveMeasurement = async (payload) => {
    try {
      if (editingMeasurement) {
        await api.patch(`/measurements/${editingMeasurement.measurement_id}`, payload);
        toast.success("Rilievo aggiornato");
        setEditingMeasurement(null);
      } else {
        const { data } = await api.post(`/clients/${id}/measurements`, payload);
        // Backend returns the measurement + attendance summary; we use it
        // to surface pack state (fresh consumption, exhaustion warning, or
        // same-date no-op).
        if (data && data.attendance_created) {
          const remaining = Number(data.sessions_remaining);
          if (remaining < 0) {
            toast.warning(`Rilievo salvato. Pacchetto sedute esaurito (${remaining} sedute).`, { duration: 6000 });
          } else {
            toast.success(`Rilievo salvato. Restano ${remaining} sedute nel pacchetto.`);
          }
        } else {
          toast.success("Rilievo salvato");
        }
        setAddingMeasurement(false);
      }
      loadAll();
    } catch (e) {
      toast.error("Errore salvataggio rilievo");
    }
  };

  if (!client) return <div className="text-sm">Caricamento…</div>;

  const counts = { profile: 1, plans: plans.length, days: compositions.length, measurements: measurements.length, photos: photos.length };

  return (
    <div data-testid="client-detail">
      <Link to="/admin" className="inline-flex items-center gap-1 label-tiny hover:text-[color:var(--accent)]">
        <CaretLeft size={14} weight="bold" /> Clienti
      </Link>

      {/* Hero */}
      <div className="mt-4 rounded-3xl border border-[color:var(--border)] p-8 bg-[color:var(--bg-primary)]">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-tiny">{client.gender === "F" ? "Femminile" : client.gender === "M" ? "Maschile" : "Genere da impostare"}</div>
            <h1 className="font-serif text-5xl tracking-tight mt-2">{client.name}</h1>
            <div className="text-sm text-[color:var(--text-secondary)] mt-1">{client.email}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="hero-new-plan" onClick={createPlan} className="rounded-full bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white">
              <ForkKnife size={14} weight="fill" className="mr-2" /> Nuovo piano
            </Button>
            <Button data-testid="hero-new-measurement" variant="outline" onClick={() => { setEditingMeasurement(null); setAddingMeasurement(true); setSection("measurements"); }} className="rounded-full">
              <Ruler size={14} weight="fill" className="mr-2" /> Aggiungi rilievo
            </Button>
            <Button data-testid="hero-new-photo" variant="outline" onClick={() => setSection("photos")} className="rounded-full">
              <Camera size={14} weight="fill" className="mr-2" /> Carica foto
            </Button>
          </div>
        </div>
      </div>

      {/* Section switcher */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="section-nav">
        {SECTIONS.map((s) => {
          const active = section === s.key;
          const Icon = s.icon;
          const count = counts[s.key];
          return (
            <button
              key={s.key}
              data-testid={`section-${s.key}`}
              onClick={() => setSection(s.key)}
              className="text-left rounded-2xl p-4 border transition-all"
              style={{
                background: active ? "var(--text-primary)" : "var(--bg-primary)",
                color: active ? "var(--bg-primary)" : "var(--text-primary)",
                borderColor: active ? "var(--text-primary)" : "var(--border)",
                transform: active ? "translateY(-2px)" : "none",
              }}
            >
              <div className="flex items-center justify-between">
                <Icon size={22} weight={active ? "fill" : "duotone"} style={{ color: active ? "var(--bg-primary)" : "var(--accent)" }} />
                {s.key !== "profile" && s.key !== "photos" && (
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                    style={{ background: active ? "rgba(252,250,248,0.15)" : "var(--bg-secondary)", color: active ? "var(--bg-primary)" : "var(--text-secondary)" }}>
                    {count}
                  </span>
                )}
              </div>
              <div className="font-serif text-xl mt-3 leading-tight">{s.label}</div>
              <div className="text-[11px] uppercase tracking-widest mt-1 opacity-70">{s.hint}</div>
            </button>
          );
        })}
      </div>

      <div className="mt-8">
        {section === "profile" && (
          <div>
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
              <div>
                <Label className="label-tiny">Nome</Label>
                <Input data-testid="profile-name" value={client.name || ""} onChange={(e) => setClient({ ...client, name: e.target.value })} />
              </div>
              <div>
                <Label className="label-tiny">Email d'accesso</Label>
                <Input
                  data-testid="profile-email"
                  type="email"
                  autoComplete="off"
                  value={client.email || ""}
                  onChange={(e) => setClient({ ...client, email: e.target.value })}
                />
                <div className="text-[10px] text-[color:var(--text-secondary)] italic mt-1">
                  Modificando l'email cambiano le credenziali d'accesso del cliente. Piani, rilievi e foto restano collegati al profilo.
                </div>
              </div>
              <div>
                <Label className="label-tiny">Genere</Label>
                <Select value={client.gender || "M"} onValueChange={(v) => setClient({ ...client, gender: v })}>
                  <SelectTrigger data-testid="profile-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Maschile</SelectItem>
                    <SelectItem value="F">Femminile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-tiny">Data di nascita</Label>
                <SmartDateInput
                  data-testid="profile-dob"
                  value={client.date_of_birth || ""}
                  onChange={(iso) => setClient({ ...client, date_of_birth: iso })}
                />
              </div>
              <div>
                <Label className="label-tiny">Altezza (cm)</Label>
                <Input type="number" value={client.height_cm || ""} onChange={(e) => setClient({ ...client, height_cm: e.target.value })} />
              </div>
              <div>
                <Label className="label-tiny">Livello di attività</Label>
                <Select
                  value={client.activity_level || ""}
                  onValueChange={(v) => setClient({ ...client, activity_level: v || null })}
                >
                  <SelectTrigger data-testid="profile-activity"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_OPTIONS.map((o) => (
                      <SelectItem key={o.key} value={o.key}>
                        {o.label} <span className="text-[color:var(--text-secondary)] text-xs">— {o.hint}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <div
                  className="rounded-xl border border-[color:var(--border)] p-4 space-y-3"
                  style={{ background: "var(--bg-secondary)" }}
                >
                  <div className="flex items-center gap-2">
                    <LockKey size={14} weight="duotone" style={{ color: "var(--accent)" }} />
                    <div className="text-sm font-medium">Password d'accesso</div>
                  </div>
                  <p className="text-xs text-[color:var(--text-secondary)]">
                    Il cliente accede con email + password. Imposta o sostituisci la password quando vuoi — <strong>lascia vuoto</strong> per non modificarla.
                  </p>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[220px]">
                      <Input
                        data-testid="profile-password"
                        type="text"
                        value={client.password || ""}
                        onChange={(e) => setClient({ ...client, password: e.target.value })}
                        placeholder="Nuova password (min. 6 caratteri)"
                      />
                    </div>
                    <button
                      type="button"
                      data-testid="generate-password-btn"
                      onClick={() => {
                        const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
                        let p = "";
                        for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
                        setClient({ ...client, password: p });
                        navigator.clipboard?.writeText(p).catch(() => {});
                        toast.success("Password generata e copiata negli appunti");
                      }}
                      className="rounded-full border border-[color:var(--border)] px-4 h-10 text-xs hover:border-[color:var(--text-primary)]"
                    >
                      Genera & copia
                    </button>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                <div
                  className="flex items-start justify-between gap-4 rounded-xl border border-[color:var(--border)] p-4"
                  style={{ background: "var(--bg-secondary)" }}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <BellRinging size={14} weight="duotone" style={{ color: "var(--accent)" }} />
                      <div className="text-sm font-medium">Notifiche email al cliente</div>
                    </div>
                    <div className="mt-1 text-xs text-[color:var(--text-secondary)]">
                      Il cliente riceverà una email quando <strong>attivi un nuovo piano</strong> o
                      salvi un <strong>nuovo rilievo</strong>. Disattiva per silenziare tutte le comunicazioni.
                    </div>
                  </div>
                  <Switch
                    data-testid="profile-email-notifications"
                    checked={client.email_notifications !== false}
                    onCheckedChange={(v) => setClient({ ...client, email_notifications: v })}
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <div
                  className="rounded-xl border p-4 space-y-3"
                  data-testid="sessions-pack-card"
                  style={{
                    borderColor: sessionsRemaining < 0 ? "#B94A4A" : "var(--border)",
                    background: sessionsRemaining < 0 ? "rgba(185,74,74,0.06)" : "var(--bg-secondary)",
                  }}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Ticket size={16} weight="duotone" style={{ color: sessionsRemaining < 0 ? "#B94A4A" : "var(--accent)" }} />
                      <div className="text-sm font-medium">Pacchetto sedute</div>
                    </div>
                    <div className="inline-flex items-center gap-3 text-xs">
                      {sessionsRemaining < 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold" style={{ background: "#B94A4A", color: "#fff" }}>
                          <Warning size={12} weight="fill" /> Esaurito
                        </span>
                      )}
                      <span className="text-[color:var(--text-secondary)]">
                        Usate <strong className="text-[color:var(--text-primary)] tabular-nums">{sessionsPaid}</strong> · Rimanenti
                      </span>
                      <span
                        data-testid="sessions-remaining-badge"
                        className="tabular-nums font-serif text-xl"
                        style={{ color: sessionsRemaining < 0 ? "#B94A4A" : "var(--text-primary)" }}
                      >
                        {sessionsRemaining}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[180px]">
                      <Label className="label-tiny">Sedute pagate nel pacchetto</Label>
                      <Input
                        data-testid="sessions-total-input"
                        type="number"
                        inputMode="numeric"
                        min="0"
                        step="1"
                        value={client.sessions_total ?? 0}
                        onChange={(e) => setClient({ ...client, sessions_total: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                    <div className="text-[10px] text-[color:var(--text-secondary)] italic max-w-md">
                      Ogni rilievo con <strong>data nuova</strong> crea automaticamente una presenza pagata che scala una seduta. Modificare un rilievo esistente sulla stessa data non genera una nuova presenza. Il saldo può andare in negativo — il salvataggio dei rilievi resta sempre consentito.
                    </div>
                  </div>
                  {attendanceDesc.length > 0 && (
                    <div className="mt-2">
                      <div className="label-tiny mb-2">Storico presenze ({attendanceDesc.length})</div>
                      <ul className="space-y-1.5 max-h-64 overflow-auto pr-1" data-testid="attendance-list">
                        {attendanceDesc.map((a) => (
                          <li
                            key={a.id}
                            data-testid={`attendance-${a.id}`}
                            className="flex items-center gap-2 rounded-lg border border-[color:var(--border)] px-3 py-1.5 text-sm bg-[color:var(--bg-primary)]"
                          >
                            <span className="tabular-nums flex-1 min-w-0">{formatItalianDate(a.date)}</span>
                            <label className="inline-flex items-center gap-1.5 cursor-pointer select-none text-xs">
                              <Switch
                                data-testid={`attendance-paid-${a.id}`}
                                checked={!!a.paid}
                                onCheckedChange={() => toggleAttendancePaid(a)}
                              />
                              <span className={a.paid ? "text-[color:var(--text-primary)]" : "text-[color:var(--text-secondary)] italic"}>
                                {a.paid ? "Pagata" : "Non pagata"}
                              </span>
                            </label>
                            <button
                              type="button"
                              data-testid={`attendance-remove-${a.id}`}
                              onClick={() => deleteAttendance(a)}
                              title="Annulla presenza (rimuove dallo storico)"
                              className="w-7 h-7 inline-flex items-center justify-center rounded-full border border-[color:var(--border)] text-red-700 hover:bg-red-50 hover:border-red-300"
                            >
                              <X size={12} weight="bold" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="label-tiny">Note</Label>
                <Textarea rows={4} value={client.notes || ""} onChange={(e) => setClient({ ...client, notes: e.target.value })} />
              </div>
            </div>
            <Button data-testid="save-profile-btn" onClick={saveProfile} disabled={saving} className="mt-6 rounded-full bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white">Salva profilo</Button>
          </div>
        )}

        {section === "plans" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="label-tiny">Piani nutrizionali</div>
              <Button data-testid="new-plan-btn" onClick={createPlan} className="rounded-full bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white">
                <Plus size={14} weight="bold" className="mr-1" /> Nuovo piano
              </Button>
            </div>
            {plans.length === 0 ? (
              <div className="border border-dashed border-[color:var(--border)] rounded-2xl p-10 text-center">
                <ForkKnife size={30} weight="duotone" className="mx-auto text-[color:var(--accent)]" />
                <div className="font-serif text-2xl mt-3">Nessun piano ancora</div>
                <p className="text-sm text-[color:var(--text-secondary)] mt-2 max-w-md mx-auto">
                  Crea il primo piano nutrizionale per {client.name}.
                </p>
                <Button data-testid="empty-new-plan-btn" onClick={createPlan} className="mt-6 rounded-full bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white">
                  <Plus size={14} weight="bold" className="mr-1" /> Crea il primo piano
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {plans.map((p) => (
                  <div key={p.plan_id} data-testid={`plan-row-${p.plan_id}`} className="flex items-center justify-between border border-[color:var(--border)] rounded-2xl p-5 bg-[color:var(--bg-primary)]">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="font-serif text-xl">{p.name}</div>
                        {p.active && (
                          <span className="text-xs px-2.5 py-0.5 rounded-full bg-[color:var(--accent-2)] text-white inline-flex items-center gap-1">
                            <StarFour size={12} weight="fill" /> Attivo
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[color:var(--text-secondary)] mt-1">
                        {(p.totals?.calories || 0)} kcal · P {(p.totals?.protein_g || 0)}g · C {(p.totals?.carbs_g || 0)}g · G {(p.totals?.fat_g || 0)}g · {(p.meals?.length || 0)} pasti
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {p.active ? (
                        <Button
                          variant="outline"
                          data-testid={`deactivate-plan-${p.plan_id}`}
                          onClick={() => deactivatePlan(p.plan_id)}
                          className="rounded-full"
                        >
                          <Star size={14} weight="fill" className="mr-1" style={{ color: "var(--accent-2)" }} /> Disattiva
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          data-testid={`activate-plan-${p.plan_id}`}
                          onClick={() => activatePlan(p.plan_id)}
                          className="rounded-full"
                        >
                          <Star size={14} weight="duotone" className="mr-1" /> Attiva
                        </Button>
                      )}
                      <Link to={`/admin/plans/${p.plan_id}`}>
                        <Button variant="outline" className="rounded-full">Apri</Button>
                      </Link>
                      <button
                        type="button"
                        data-testid={`delete-plan-${p.plan_id}`}
                        onClick={() => deletePlan(p.plan_id, p.name)}
                        className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-[color:var(--border)] text-red-700 hover:bg-red-50 hover:border-red-300 transition-colors"
                        title="Elimina piano"
                      >
                        <TrashSimple size={16} weight="bold" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {section === "days" && (
          <div>
            {/* Read-only view of the client's dynamic-porzionario saves.
                Admin can inspect what the client composed, delete
                erroneous entries, and open the porzionario editor to
                modify a saved day directly. All grams are already
                computed on the client side; here we display them verbatim. */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="label-tiny inline-flex items-center gap-1.5">
                <ClockCounterClockwise size={14} weight="duotone" style={{ color: "var(--accent)" }} />
                Salvataggi porzionario dinamico ({compositions.length})
              </div>
              <Button
                data-testid="admin-new-day-btn"
                onClick={() => navigate(`/admin/clients/${id}/porzionario`)}
                className="rounded-full bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white"
                disabled={plans.length === 0}
                title={plans.length === 0 ? "Il cliente non ha piani attivi" : "Componi una giornata come il cliente"}
              >
                <Plus size={14} weight="bold" className="mr-1" /> Nuova giornata
              </Button>
            </div>
            {compositions.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-[color:var(--border)] p-8 text-center text-sm text-[color:var(--text-secondary)]" data-testid="empty-compositions">
                Il cliente non ha ancora salvato alcuna giornata dal porzionario dinamico.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="compositions-list">
                {compositions.map((c) => {
                  const isOpen = openCompositionId === c.composition_id;
                  const totalItems = (c.meals || []).reduce(
                    (s, m) => s + Object.values(m.selections || {}).reduce((ss, arr) => ss + (arr?.length || 0), 0), 0,
                  );
                  return (
                    <li
                      key={c.composition_id}
                      data-testid={`composition-${c.composition_id}`}
                      className="rounded-xl border border-[color:var(--border)] bg-[color:var(--bg-primary)]"
                    >
                      <div className="flex items-center gap-2 px-3 py-2.5">
                        <button
                          type="button"
                          onClick={() => setOpenCompositionId(isOpen ? null : c.composition_id)}
                          className="flex-1 min-w-0 text-left"
                          data-testid={`composition-toggle-${c.composition_id}`}
                        >
                          <div className="font-medium truncate">{c.plan_name || c.label}</div>
                          <div className="mt-0.5 text-[11px] tabular-nums text-[color:var(--text-secondary)] inline-flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1">
                              <Clock size={10} weight="fill" /> {(() => { const [y,mo,dd] = (c.date||"").split("-"); return y ? `${dd}/${mo}/${y}` : c.date || ""; })()}
                            </span>
                            <span>· {totalItems} aliment{totalItems === 1 ? "o" : "i"}</span>
                            {c.updated_at && <span>· modificato</span>}
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/clients/${id}/porzionario?comp=${c.composition_id}`)}
                          data-testid={`composition-edit-${c.composition_id}`}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-[color:var(--border)] hover:border-[color:var(--text-primary)] transition-colors"
                          title="Modifica salvataggio"
                        >
                          <PencilSimple size={14} weight="duotone" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteComposition(c)}
                          data-testid={`composition-delete-${c.composition_id}`}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-[color:var(--border)] text-red-700 hover:bg-red-50 hover:border-red-300"
                          title="Elimina salvataggio"
                        >
                          <TrashSimple size={14} weight="bold" />
                        </button>
                      </div>
                      {isOpen && (
                        <div className="border-t border-[color:var(--border)] px-3 py-3 space-y-3 text-sm" data-testid={`composition-detail-${c.composition_id}`}>
                          {(c.meals || []).length === 0 ? (
                            <div className="text-xs italic text-[color:var(--text-secondary)]">Nessun pasto salvato.</div>
                          ) : (c.meals || []).map((m) => {
                            const cats = Object.entries(m.selections || {}).filter(([, arr]) => (arr || []).length > 0);
                            if (cats.length === 0) return null;
                            return (
                              <div key={m.meal_id}>
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] tabular-nums" style={{ background: "var(--text-primary)", color: "#fff" }}>
                                    <Clock size={9} weight="fill" />
                                    {m.meal_time || "—"}
                                  </div>
                                  <div className="font-serif text-base truncate">{m.meal_name}</div>
                                </div>
                                <div className="pl-1 space-y-1.5">
                                  {cats.map(([cat, arr]) => (
                                    <div key={cat}>
                                      <div className="label-tiny mb-0.5">{cat}</div>
                                      <ul className="space-y-0.5 pl-2">
                                        {arr.map((it, i) => (
                                          <li key={`${it.food_id || it.name || "item"}-${i}`} className="flex justify-between gap-3 text-xs">
                                            <span className="min-w-0 flex-1 break-words leading-snug">
                                              {(it.name || "").replace(/\s+2\s*$/, "")}
                                              {/\s+2\s*$/.test(it.name || "") && <sup className="font-bold ml-0.5 text-[0.7em] align-super">2</sup>}
                                            </span>
                                            <span className="tabular-nums font-semibold flex-shrink-0" style={{ color: "var(--accent)" }}>
                                              {it.computed_grams}g
                                            </span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {section === "measurements" && (
          <div className={measurementFormOpen ? "grid lg:grid-cols-[minmax(0,1fr)_460px] gap-8" : ""}>
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="label-tiny">Storico rilievi ({measurements.length})</div>
                <div className="flex items-center gap-2">
                <MeasurementsCompare measurements={measurements} gender={client.gender || "M"} />
                {!measurementFormOpen && (
                  <Button
                    data-testid="open-new-measurement-btn"
                    onClick={() => { setEditingMeasurement(null); setAddingMeasurement(true); }}
                    className="rounded-full bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white"
                  >
                    <Plus size={14} weight="bold" className="mr-1" /> Nuovo rilievo
                  </Button>
                )}
                </div>
              </div>
              <MeasurementsChart data={measurements} gender={client.gender || "M"} />
              <div className="mt-6">
                <BmiTdeeCard
                  client={client}
                  latestMeasurement={measurementsDesc[0] || null}
                  editHref={`/admin/clients/${id}`}
                />
              </div>
              <div className="mt-8 space-y-3">
                {measurements.length === 0 && !measurementFormOpen && (
                  <div className="text-sm text-[color:var(--text-secondary)]">Nessun rilievo salvato. Clicca «Nuovo rilievo» per inserire il primo.</div>
                )}
                {measurementsDesc.map((m) => {
                  const isEditing = editingMeasurement?.measurement_id === m.measurement_id;
                  return (
                    <div
                      key={m.measurement_id}
                      data-testid={`measurement-row-${m.measurement_id}`}
                      className="border rounded-2xl p-4 flex items-center justify-between text-sm bg-[color:var(--bg-primary)]"
                      style={{ borderColor: isEditing ? "var(--accent)" : "var(--border)" }}
                    >
                      <div>
                        <div className="font-sans text-base tabular-nums" data-testid={`measurement-date-${m.measurement_id}`}>{formatItalianDate(m.date)}{isEditing && <span className="ml-2 text-xs text-[color:var(--accent)] normal-nums">in modifica</span>}</div>
                        <div className="text-xs text-[color:var(--text-secondary)]">Peso {m.peso_kg}kg · BF {(m.bodyfat_pct * 100).toFixed(1)}% · MM {m.massa_magra_kg}kg</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          data-testid={`edit-measurement-${m.measurement_id}`}
                          onClick={() => { setEditingMeasurement(m); setAddingMeasurement(false); }}
                          className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-[color:var(--border)] hover:border-[color:var(--text-primary)] transition-colors"
                          title="Modifica rilievo"
                        >
                          <PencilSimple size={15} weight="duotone" />
                        </button>
                        <button
                          type="button"
                          data-testid={`delete-measurement-${m.measurement_id}`}
                          onClick={() => deleteMeasurement(m.measurement_id, m.date)}
                          className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-[color:var(--border)] text-red-700 hover:bg-red-50 hover:border-red-300 transition-colors"
                          title="Elimina rilievo"
                        >
                          <TrashSimple size={15} weight="bold" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {measurementFormOpen && (
              <div>
                <MeasurementForm
                  key={editingMeasurement?.measurement_id || "new"}
                  gender={client.gender || "M"}
                  initial={editingMeasurement}
                  onSave={saveMeasurement}
                  onCancel={() => { setEditingMeasurement(null); setAddingMeasurement(false); }}
                />
              </div>
            )}
          </div>
        )}

        {section === "photos" && (
          <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-8">
            <div>
              <div className="label-tiny mb-4">Galleria</div>
              <PhotoGallery photos={photos} onDelete={deletePhoto} onEdited={loadAll} />
            </div>
            <div>
              <div className="label-tiny mb-4"><Camera size={14} weight="duotone" className="inline mr-1" /> Nuova foto</div>
              <PhotoUploader clientId={id} onUploaded={() => { toast.success("Foto caricata"); loadAll(); }} />
            </div>
          </div>
        )}
      </div>

      {dialog}
    </div>
  );
}
